import { useTranslation } from "react-i18next";
import {
  formatAge,
  yardControllerError,
  yardDownCause,
  type YardDownCause,
  type YardHealth,
} from "./yardOperation";

// Un ícono por causa, escrito completo: Tailwind y remixicon se resuelven por
// el texto del fuente, igual que LINK_PALETTE. El ícono no es decoración — es lo
// que deja distinguir las dos fallas sin leer: la computadora (la PC del sitio)
// contra el chip (la controladora).
const CAUSE_ICON: Record<YardDownCause, string> = {
  pc: "ri-computer-line",
  controladora: "ri-cpu-line",
  mixto: "ri-error-warning-line",
};

/**
 * El renglón que dice QUIÉN no responde, debajo de la píldora de enlace.
 *
 * Vive en un componente aparte porque lo pintan la tarjeta
 * (YardOperationSummary) y la modal (YardOperationPanel), y las dos tienen que
 * decir exactamente lo mismo: dos copias del texto son dos frases que se
 * desincronizan en el próximo cambio. Toda la decisión —si hay causa, cuál, y
 * si el error crudo viene al caso— está en `yardDownCause` /
 * `yardControllerError`, que son puras y tienen tests; acá sólo se arma el
 * texto.
 *
 * No renderiza nada cuando no hay causa: backend viejo, nada caído, o un valor
 * que este cliente no conoce.
 */
export default function YardDownCauseLine({
  health,
  className,
}: {
  health: YardHealth | null | undefined;
  /** Color del texto: lo decide quien lo aloja, que es quien conoce el tema. */
  className: string;
}) {
  const { t } = useTranslation("truckYard");

  const cause = yardDownCause(health);
  if (!cause) return null;

  // El último latido se muestra en las tres causas, y en `controladora` es
  // donde más pesa: "último latido hace 22 s" al lado de un patio caído es la
  // prueba de que la PC está viva y de que ir a reiniciarla no arregla nada.
  const pcAge = formatAge(health?.pc_last_seen_age_ms);

  // El error crudo del SDK ("The device is off-line…") va de tooltip y nunca de
  // etiqueta: está en inglés, es del fabricante y a un operador no le dice qué
  // hacer. A quien da soporte sí, y por eso no se descarta.
  const error = yardControllerError(health);
  const title = [
    t(`yards.health.causeHint.${cause}`),
    error ? t("yards.health.controllerError", { error }) : null,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <span
      className={`inline-flex items-start gap-1 text-[11px] min-w-0 ${className}`}
      title={title}
    >
      <i className={`${CAUSE_ICON[cause]} flex-shrink-0`}></i>
      <span className="min-w-0">
        {t(`yards.health.cause.${cause}`)}
        {pcAge && <> · {t("yards.health.pcLastSeen", { age: pcAge })}</>}
      </span>
    </span>
  );
}
